/**
 * Email-the-current-report affordance — sibling to
 * `DownloadPdfButton`. Mounted on every report page that already
 * has a download icon, so the user can ALSO ship the same PDF
 * to their inbox without leaving the page (useful for forwarding,
 * archiving, mobile-from-laptop hand-off).
 *
 * Visual contract:
 *   - Icon-only, `ghost / icon-sm` to sit flush with the existing
 *     download icon + the other right-rail icons.
 *   - Click → IMMEDIATE POST (no confirm dialog). Modern SaaS
 *     pattern: an accidental click costs you one email in your
 *     own inbox; a confirm modal in front of "email me this" is
 *     friction with no payoff. The previous dialog was dropped
 *     in the link+PDF + click-to-toast pass.
 *   - In flight → icon swaps to a spinner, button disabled.
 *   - Success → small "Email sent" badge appears beside the
 *     button for a few seconds, then fades.
 *   - Failure → inline alert with the server's error message
 *     (role="alert" so screen readers are notified).
 *
 * The email itself carries:
 *   - A clickable link to the live in-app report (always
 *     current data; mobile-friendly), AND
 *   - The PDF as an attachment (snapshot at request time;
 *     forwardable / archivable / offline-readable).
 *
 * Recipient is implicit — the backend sends to the requesting
 * user's email on file. The address is intentionally NOT shown
 * in the success badge — the user already knows their own
 * email; surfacing it is visual noise. Multi-recipient
 * ("CC a teammate") is a clear v2; v1 keeps the surface tiny.
 *
 * Renders nothing when this installation cannot send email, on every
 * screen that mounts it. The server refuses the request in that case
 * too; hiding the button keeps the screen from offering it.
 */
import { useState } from 'react'
import { Mail, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui'
import { useEmailStatus } from '@/hooks/usePublicSettings'

export interface EmailPdfButtonProps {
  /** The POST endpoint that sends the email (mirrors the GET PDF
   * endpoint at the same URL minus the `/email` suffix). */
  url: string
  /** Tooltip + aria-label. Defaults to "Email this report".
   * Pass page-specific phrasing for clarity (e.g. "Email this
   * run summary"). */
  title?: string
}

export function EmailPdfButton({
  url,
  title = 'Email this report',
}: EmailPdfButtonProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const emailStatus = useEmailStatus()

  async function handleClick() {
    if (loading) return
    setLoading(true)
    setError(null)
    setSent(false)
    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
      })
      const body: { ok?: boolean; error?: string; message?: string } =
        await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(
          body.message || body.error || `Failed to email report (${res.status})`,
        )
      }
      setSent(true)
      // Auto-clear the success badge after a few seconds.
      setTimeout(() => setSent(false), 5000)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  if (emailStatus !== 'on') return null

  return (
    <div className="flex items-center gap-2">
      {error && (
        <span
          role="alert"
          className="max-w-xs truncate text-xs text-destructive"
          title={error}
        >
          {error}
        </span>
      )}
      {sent && !error && (
        <span className="text-xs text-emerald-700">Email sent</span>
      )}
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={handleClick}
        disabled={loading}
        title={loading ? 'Sending…' : title}
        aria-label={loading ? 'Sending email' : title}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Mail className="h-4 w-4" />
        )}
      </Button>
    </div>
  )
}
